import React from 'react';
import './Skeletons.scss';

// Store Product Card Skeleton - matches your exact design
export const ProductCardSkeleton = () => (
  <div className="store-product-card-skeleton">
    {/* Matches your Card component with padding 2 */}
    <div className="store-card-content">
      {/* Category name skeleton - matches Typography variant="subtitle2" */}
      <div className="store-skeleton-category"></div>
      
      {/* Product name skeleton - matches Typography variant="h6" with your styling */}
      <div className="store-skeleton-product-name"></div>
      
      {/* BaseUOM skeleton - matches Typography variant="body2" */}
      <div className="store-skeleton-uom"></div>
      
      {/* Stock status area */}
      <div className="store-skeleton-stock-area">
        <div className="store-skeleton-stock-dot"></div>
        <div className="store-skeleton-stock-text"></div>
      </div>
      
      {/* Price and quantity section */}
      <div className="store-skeleton-bottom-section">
        <div className="store-skeleton-price"></div>
        <div className="store-skeleton-quantity-controls">
          <div className="store-skeleton-minus-btn"></div>
          <div className="store-skeleton-qty-number"></div>
          <div className="store-skeleton-plus-btn"></div>
        </div>
      </div>
    </div>
  </div>
);

// Store Product Card Skeleton for Headers.js style (with image)
export const ProductCardWithImageSkeleton = () => (
  <div className="store-product-card-with-image-skeleton">
    <div className="store-card-flex-content">
      {/* 80x80 image skeleton - matches your CardMedia */}
      <div className="store-skeleton-product-image"></div>
      
      <div className="store-skeleton-product-info">
        {/* Product name - matches your Typography with fontSize 13px */}
        <div className="store-skeleton-product-name-small"></div>
        
        <div className="store-skeleton-price-controls">
          {/* Price - matches fontSize 15px */}
          <div className="store-skeleton-price-small"></div>
          
          {/* Control buttons - matches your IconButton design */}
          <div className="store-skeleton-controls">
            <div className="store-skeleton-control-btn store-skeleton-minus"></div>
            <div className="store-skeleton-qty-small"></div>
            <div className="store-skeleton-control-btn store-skeleton-plus"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Facebook-style skeleton for profile section
export const ProfileSkeleton = () => (
  <div className="skeleton-profile">
    <div className="skeleton-avatar"></div>
    <div className="skeleton-profile-info">
      <div className="skeleton-name"></div>
      <div className="skeleton-text skeleton-text-short"></div>
    </div>
  </div>
);

// Store Order Card Skeleton - matches your exact order design
export const OrderSkeleton = () => (
  <div className="store-order-card-skeleton">
    {/* Header section with order info */}
    <div className="store-order-header">
      <div className="store-order-left">
        <div className="store-skeleton-order-number"></div>
        <div className="store-skeleton-order-date"></div>
      </div>
      <div className="store-skeleton-order-status-badge"></div>
    </div>
    
    {/* Order items section */}
    <div className="store-order-items">
      <div className="store-order-item">
        <div className="store-skeleton-item-icon"></div>
        <div className="store-skeleton-item-details">
          <div className="store-skeleton-item-name"></div>
          <div className="store-skeleton-item-qty"></div>
        </div>
        <div className="store-skeleton-item-price"></div>
      </div>
    </div>
    
    {/* Bottom section with total and actions */}
    <div className="store-order-footer">
      <div className="store-skeleton-total-amount"></div>
      <div className="store-skeleton-action-buttons">
        <div className="store-skeleton-btn-small"></div>
        <div className="store-skeleton-btn-primary"></div>
      </div>
    </div>
  </div>
);

// Facebook-style skeleton for categories
export const CategorySkeleton = () => (
  <div className="skeleton-category">
    <div className="skeleton-category-image"></div>
    <div className="skeleton-category-name"></div>
  </div>
);

// Facebook-style skeleton for checkout
export const CheckoutSkeleton = () => (
  <div className="skeleton-checkout">
    <div className="skeleton-checkout-header">
      <div className="skeleton-title"></div>
    </div>
    <div className="skeleton-checkout-items">
      {[1, 2, 3].map(i => (
        <div key={i} className="skeleton-checkout-item">
          <div className="skeleton-item-image"></div>
          <div className="skeleton-item-details">
            <div className="skeleton-text"></div>
            <div className="skeleton-text skeleton-text-short"></div>
          </div>
          <div className="skeleton-item-price"></div>
        </div>
      ))}
    </div>
    <div className="skeleton-checkout-total">
      <div className="skeleton-text skeleton-text-medium"></div>
      <div className="skeleton-button"></div>
    </div>
  </div>
);

// Store Product Grid Skeleton - matches your exact grid layout
export const ProductGridSkeleton = ({ count = 6, withImages = false }) => (
  <div className="store-product-grid-skeleton">
    {Array.from({ length: count }, (_, i) => (
      withImages ? <ProductCardWithImageSkeleton key={i} /> : <ProductCardSkeleton key={i} />
    ))}
  </div>
);

// Store Category Content Skeleton - matches your CategoryComponent layout
export const CategoryContentSkeleton = () => (
  <div className="store-category-content-skeleton">
    {/* Matches your Box with grid layout */}
    <div className="store-grid-container">
      <ProductGridSkeleton count={8} />
    </div>
  </div>
);

// Main content skeleton wrapper - matches your store components
export const ContentSkeleton = ({ type, count = 1, withImages = false }) => {
  const renderSkeleton = () => {
    switch (type) {
      case 'product-card':
        return withImages ? <ProductCardWithImageSkeleton /> : <ProductCardSkeleton />;
      case 'product-grid':
        return <ProductGridSkeleton count={count} withImages={withImages} />;
      case 'profile':
        return <ProfileSkeleton />;
      case 'orders':
        return (
          <div className="store-orders-skeleton-container">
            {Array.from({ length: count }, (_, i) => <OrderSkeleton key={i} />)}
          </div>
        );
      case 'checkout':
        return <CheckoutSkeleton />;
      case 'categories':
        return Array.from({ length: count }, (_, i) => <CategorySkeleton key={i} />);
      case 'category-content':
        return <CategoryContentSkeleton />;
      default:
        return <ProductCardSkeleton />;
    }
  };

  return (
    <div className="store-skeleton-wrapper">
      {renderSkeleton()}
    </div>
  );
};

export default ContentSkeleton;
